import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import LanguageSwitcher from 'shared/components/LanguageSwitcher';

const HAAT_LOGO_URL =
  'https://cdn.prod.website-files.com/6829acf870093a50cc6ceb69/682b03419ead6e45d8f2c59e_logo%20(2).svg';

interface HeaderProps {
  /** Label for the action that is always visible beside the language switcher. */
  persistentActionLabel?: string;
  /** Click handler for the always-visible action. */
  onPersistentActionClick?: () => void;
  /** Label for the optional CTA button rendered on the end side of the header. */
  ctaLabel?: string;
  /** Click handler for the CTA button. */
  onCtaClick?: () => void;
  /** When true, reveals the CTA button (e.g. after the user scrolls past the hero). */
  showCta?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  persistentActionLabel,
  onPersistentActionClick,
  ctaLabel,
  onCtaClick,
  showCta = false,
}) => {
  const { t } = useTranslation();

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -40, opacity: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="sticky top-0 z-50 w-full"
    >
      <div className="absolute inset-0 bg-white/70 backdrop-blur-xl border-b border-gray-200/60" />
      <nav className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src={HAAT_LOGO_URL}
            alt={t('appName') ?? 'HAAT'}
            className="h-8 w-auto"
            loading="eager"
            decoding="async"
          />
          <span className="sr-only">{t('appName') ?? 'HAAT'}</span>
        </div>

        <div className="flex items-center gap-3">
          {persistentActionLabel && (
            <button
              type="button"
              onClick={onPersistentActionClick}
              className="rounded-full border border-gray-300 bg-white/80 px-4 py-2 text-sm font-semibold text-gray-800 transition-colors hover:border-haat-red-300 hover:text-haat-red-700"
            >
              {persistentActionLabel}
            </button>
          )}
          <AnimatePresence>
            {showCta && ctaLabel && (
              <motion.button
                key="header-cta"
                type="button"
                onClick={onCtaClick}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                className="rounded-full bg-[#c20037] px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#a8002f]"
              >
                {ctaLabel}
              </motion.button>
            )}
          </AnimatePresence>
          <LanguageSwitcher variant="header" />
        </div>
      </nav>
    </motion.header>
  );
};

export default Header;
